/**
 * Estadísticas derivadas (FR-STAT). Todas son funciones puras de UEResult[].
 */
import { diffDays, isoWeekday, type LocalDate } from './dates.js';
import type { HabitDef, UEKind, UEResult, UEStatus } from './types.js';

const NEUTRAL: ReadonlySet<UEStatus> = new Set(['not_scheduled', 'out_of_range', 'skipped', 'vacation', 'pending']);

export interface StreakInfo {
  current: number;
  best: number;
  unit: UEKind;
}

export function streaks(results: UEResult[]): StreakInfo {
  const sorted = [...results].sort((a, b) => (a.start < b.start ? -1 : 1));
  let run = 0;
  let best = 0;
  for (const r of sorted) {
    if (NEUTRAL.has(r.status)) continue;
    if (r.status === 'completed') {
      run++;
      if (run > best) best = run;
    } else {
      run = 0;
    }
  }
  const unit: UEKind = sorted[sorted.length - 1]?.kind ?? 'day';
  return { current: run, best, unit };
}

export interface RateInfo {
  completed: number;
  missed: number;
  failed: number;
  skipped: number;
  vacation: number;
  /** null cuando no hay denominador (se muestra "—"). */
  rate: number | null;
}

function inRange(r: UEResult, range?: { from?: LocalDate; to?: LocalDate }): boolean {
  if (!range) return true;
  if (range.from && r.end < range.from) return false;
  if (range.to && r.start > range.to) return false;
  return true;
}

/**
 * Porcentaje de éxito: completed / (completed + missed + failed).
 * La UE abierta solo cuenta si su estado ya es definitivo (FR-STAT-03).
 */
export function successRate(results: UEResult[], range?: { from?: LocalDate; to?: LocalDate }): RateInfo {
  const info: RateInfo = { completed: 0, missed: 0, failed: 0, skipped: 0, vacation: 0, rate: null };
  for (const r of results) {
    if (!inRange(r, range)) continue;
    if (r.open && !r.final) continue;
    if (r.status === 'completed') info.completed++;
    else if (r.status === 'missed') info.missed++;
    else if (r.status === 'failed') info.failed++;
    else if (r.status === 'skipped') info.skipped++;
    else if (r.status === 'vacation') info.vacation++;
  }
  const denom = info.completed + info.missed + info.failed;
  info.rate = denom === 0 ? null : info.completed / denom;
  return info;
}

/** Progreso medio en UE no neutrales (FR-STAT-04). */
export function averageProgress(results: UEResult[], range?: { from?: LocalDate; to?: LocalDate }): number | null {
  const rs = results.filter((r) => inRange(r, range) && !NEUTRAL.has(r.status));
  if (rs.length === 0) return null;
  return rs.reduce((acc, r) => acc + r.progress, 0) / rs.length;
}

/** Días desde la última UE incumplida (hábitos quit, FR-STAT-05). */
export function daysSinceLastRelapse(habit: HabitDef, results: UEResult[], today: LocalDate): number {
  let last: LocalDate | null = null;
  for (const r of results) {
    if ((r.status === 'missed' || r.status === 'failed') && (!last || r.end > last)) last = r.end;
  }
  if (!last) return Math.max(0, diffDays(habit.startDate, today));
  return Math.max(0, diffDays(last, today));
}

export interface WeekdayRate {
  /** Día ISO de la semana: 1 = lunes … 7 = domingo. */
  weekday: number;
  completed: number;
  total: number;
  /** null cuando no hay denominador en ese día. */
  rate: number | null;
}

/**
 * Patrón por día de la semana (FR-STAT-09): porcentaje de éxito agrupado por el día
 * en que empieza la UE. Solo entran las UE diarias, porque una UE semanal o mensual
 * no pertenece a un único día. Usa el mismo denominador que `successRate`.
 */
export function weekdayPattern(results: UEResult[], range?: { from?: LocalDate; to?: LocalDate }): WeekdayRate[] {
  const out: WeekdayRate[] = Array.from({ length: 7 }, (_, i) => ({ weekday: i + 1, completed: 0, total: 0, rate: null }));
  for (const r of results) {
    if (r.kind !== 'day' || !inRange(r, range)) continue;
    if (r.open && !r.final) continue;
    if (r.status !== 'completed' && r.status !== 'missed' && r.status !== 'failed') continue;
    const slot = out[isoWeekday(r.start) - 1]!;
    slot.total++;
    if (r.status === 'completed') slot.completed++;
  }
  for (const s of out) s.rate = s.total === 0 ? null : s.completed / s.total;
  return out;
}

export interface GapInfo {
  /** Interrupciones: tramos seguidos de UE incumplidas. */
  count: number;
  /** Longitud del tramo más largo, en UE. */
  longest: number;
  unit: UEKind;
}

/**
 * Huecos del periodo: cuántas veces se cortó la constancia y cuánto duró el corte más
 * largo. Misma regla que las rachas (FR-STAT-01): las UE neutrales ni cortan ni cuentan,
 * y la UE abierta solo entra si su estado ya es definitivo.
 */
export function gaps(results: UEResult[], range?: { from?: LocalDate; to?: LocalDate }): GapInfo {
  const sorted = results.filter((r) => inRange(r, range) && !(r.open && !r.final)).sort((a, b) => (a.start < b.start ? -1 : 1));
  const info: GapInfo = { count: 0, longest: 0, unit: sorted[sorted.length - 1]?.kind ?? 'day' };
  let run = 0;
  for (const r of sorted) {
    if (NEUTRAL.has(r.status)) continue;
    if (r.status === 'completed') {
      run = 0;
      continue;
    }
    if (run === 0) info.count++;
    run++;
    if (run > info.longest) info.longest = run;
  }
  return info;
}

export function totalCompletions(results: UEResult[]): number {
  return results.filter((r) => r.status === 'completed').length;
}

export interface Achievement {
  id: string;
  /** Fecha (inicio de la UE) en que se alcanzó por primera vez. */
  achievedOn: LocalDate;
}

const STREAK_MILESTONES = [3, 7, 21, 30, 60, 100, 200, 365];
const COMPLETION_MILESTONES = [1, 10, 50, 100, 250, 500, 1000];

/** Logros derivados (FR-ACH-02): se recalculan siempre a partir de los resultados. */
export function achievements(results: UEResult[]): Achievement[] {
  const sorted = [...results].sort((a, b) => (a.start < b.start ? -1 : 1));
  const out: Achievement[] = [];
  const got = new Set<string>();
  let run = 0;
  let total = 0;
  for (const r of sorted) {
    if (NEUTRAL.has(r.status)) continue;
    if (r.status === 'completed') {
      run++;
      total++;
      for (const m of STREAK_MILESTONES) {
        const id = `streak_${m}`;
        if (run >= m && !got.has(id)) {
          got.add(id);
          out.push({ id, achievedOn: r.start });
        }
      }
      for (const m of COMPLETION_MILESTONES) {
        const id = `total_${m}`;
        if (total >= m && !got.has(id)) {
          got.add(id);
          out.push({ id, achievedOn: r.start });
        }
      }
    } else {
      run = 0;
    }
  }
  return out;
}
